/**
 * Collection Context
 * 
 * Provides collection state and actions to child components.
 * Enables deeply nested components to access collection functionality
 * without prop drilling.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useCollectionState, type UseCollectionStateOptions } from './useCollectionState';
import type { CollectionState, CollectionActions } from './types';

// ============================================================================
// Context Types
// ============================================================================

interface CollectionContextValue<T, SortField = string, FilterField = string, GroupField = string> {
  state: CollectionState<T, SortField, FilterField, GroupField>;
  actions: CollectionActions<T, SortField, FilterField, GroupField>;
  getId: (item: T) => string;
}

// ============================================================================
// Context Creation
// ============================================================================

// Using `any` for the context to allow flexible typing at consumption site
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CollectionContext = createContext<CollectionContextValue<any, any, any, any> | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface CollectionProviderProps<T, SortField = string, FilterField = string, GroupField = string> 
  extends UseCollectionStateOptions<T, SortField, FilterField, GroupField> {
  children: ReactNode;
}

export function CollectionProvider<T, SortField = string, FilterField = string, GroupField = string>({
  children,
  ...options
}: CollectionProviderProps<T, SortField, FilterField, GroupField>) {
  const [state, actions] = useCollectionState<T, SortField, FilterField, GroupField>(options);
  
  const value: CollectionContextValue<T, SortField, FilterField, GroupField> = {
    state,
    actions,
    getId: options.getId,
  };
  
  return (
    <CollectionContext.Provider value={value}>
      {children}
    </CollectionContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Access the full collection context (state + actions).
 * Must be used within a CollectionProvider.
 */
export function useCollection<T, SortField = string, FilterField = string, GroupField = string>(): 
  CollectionContextValue<T, SortField, FilterField, GroupField> {
  const context = useContext(CollectionContext);
  
  if (!context) {
    throw new Error('useCollection must be used within a CollectionProvider');
  }
  
  return context as CollectionContextValue<T, SortField, FilterField, GroupField>;
}

/**
 * Access only the collection state (read-only).
 */
export function useCollectionState_<T, SortField = string, FilterField = string, GroupField = string>(): 
  CollectionState<T, SortField, FilterField, GroupField> {
  return useCollection<T, SortField, FilterField, GroupField>().state;
}

/**
 * Access only the collection actions.
 */
export function useCollectionActions<T, SortField = string, FilterField = string, GroupField = string>(): 
  CollectionActions<T, SortField, FilterField, GroupField> {
  return useCollection<T, SortField, FilterField, GroupField>().actions;
}

/**
 * Access selection state and actions.
 */
export function useCollectionSelection() {
  const { state, actions } = useCollection();
  
  return {
    selectedIds: state.selection.selectedIds,
    selectedCount: state.selection.selectedIds.size,
    isAllSelected: state.selection.isAllSelected,
    isIndeterminate: state.selection.isIndeterminate,
    ...actions.selection,
  };
}

/**
 * Check if a specific item is selected.
 */
export function useIsSelected(itemId: string): boolean {
  const { state } = useCollection();
  return state.selection.selectedIds.has(itemId);
}

/**
 * Get the current view mode.
 */
export function useViewMode() {
  const { state, actions } = useCollection();
  return {
    viewMode: state.viewMode,
    setViewMode: actions.setViewMode,
  };
}

/**
 * Get the current item count state.
 */
export function useItemCountState() {
  const { state } = useCollection();
  return {
    itemCount: state.itemCount,
    filteredCount: state.filteredCount,
    itemCountState: state.itemCountState,
  };
}

export { CollectionContext };
